
import React, { createContext, useContext, useState, ReactNode } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import useMetaTracking from "@/hooks/use-meta-tracking";

interface ServiceSelectionContextType {
  selectedServices: {
    boleto: boolean;
    antecipacao: boolean;
  };
  showError: boolean;
  showDialog: boolean;
  handleServiceSelection: (service: 'boleto' | 'antecipacao') => void;
  handlePlanSelection: (plan: string) => void;
  setShowDialog: (show: boolean) => void;
  handleRegistrationOption: (option: 'express' | 'assisted') => void;
}

const ServiceSelectionContext = createContext<ServiceSelectionContextType | undefined>(undefined);

export const ServiceSelectionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [selectedServices, setSelectedServices] = useState<{
    boleto: boolean;
    antecipacao: boolean;
  }>({
    boleto: false,
    antecipacao: false
  });
  
  const [showError, setShowError] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const navigate = useNavigate();
  const { trackSubmitApplication } = useMetaTracking();

  const handleServiceSelection = (service: 'boleto' | 'antecipacao') => {
    setSelectedServices(prev => ({
      ...prev,
      [service]: !prev[service]
    }));
    setShowError(false);
  };

  const handlePlanSelection = (plan: string) => {
    if (!selectedServices.boleto && !selectedServices.antecipacao) {
      setShowError(true);
      toast.error("Selecione pelo menos um serviço");
      return;
    }
    setShowError(false);

    // Track the SubmitApplication event before redirecting
    trackSubmitApplication({
      plan_name: plan,
      services: selectedServices.boleto && selectedServices.antecipacao 
        ? 'boleto_e_antecipacao' 
        : selectedServices.boleto 
          ? 'boleto' 
          : 'antecipacao',
      action: 'plan_selection'
    });

    if (selectedServices.boleto && !selectedServices.antecipacao) {
      navigate("/cadastro-express");
      return;
    }

    window.location.href = "https://portal.drcash.com.br/new-lead";
  };

  const handleRegistrationOption = (option: 'express' | 'assisted') => {
    setShowDialog(false);
    
    // Track the SubmitApplication event for registration option selection
    trackSubmitApplication({
      registration_type: option,
      services: selectedServices.boleto && selectedServices.antecipacao 
        ? 'boleto_e_antecipacao' 
        : selectedServices.boleto 
          ? 'boleto' 
          : 'antecipacao',
      action: 'registration_option'
    });
    
    if (option === 'express') {
      navigate("/cadastro-express");
    } else {
      window.location.href = "https://portal.drcash.com.br/new-lead";
    }
  };

  return (
    <ServiceSelectionContext.Provider
      value={{
        selectedServices,
        showError,
        showDialog,
        handleServiceSelection,
        handlePlanSelection,
        setShowDialog,
        handleRegistrationOption,
      }}
    >
      {children}
    </ServiceSelectionContext.Provider>
  );
};

export const useServiceSelection = () => {
  const context = useContext(ServiceSelectionContext);
  if (context === undefined) {
    throw new Error('useServiceSelection must be used within a ServiceSelectionProvider');
  }
  return context;
};
